<!-- ===================== Body: sidebar + stacked sections ===================== -->
<!-- [boxes] (the palette page, owner 2026-10-15): the tool pages' design, a sticky line of chips in place of the
     sidebar and every section as two cards, its head at the left and its content at the right (tool-boxes.scss) -->
<nav class="cp-chipbar" appStuck *ngIf="boxes && (palette.length > 0 || bundleMode)" aria-label="Palette sections">
  <div class="cp-chipbar-row">
    <a class="pl-chip" [class.on]="activeSection === 'top'" href="#" (click)="$event.preventDefault(); scrollToSection( 'top' )">
      <span>{{ overviewLabel }}</span>
    </a>
    <a *ngFor="let s of pageSections" class="pl-chip" [class.on]="activeSection === s.key" [attr.href]="'#sec-' + s.key"
       (click)="$event.preventDefault(); scrollToSection( s.key )">
      <span>{{ s.label }}</span>
    </a>
  </div>
</nav>

<div class="cp-body" [class.cp-boxes]="boxes" *ngIf="palette.length > 0 || bundleMode">
  <aside class="cp-sidebar" *ngIf="!boxes">
    <p class="cp-sidebar-title">On This Page</p>
    <button type="button" class="cp-sidebar-color" [class.active]="activeSection === 'top'"
            (click)="scrollToSection( 'top' )" [attr.aria-label]="'Scroll to ' + overviewLabel">
      <span class="cp-sidebar-swatch" [style.background]="paletteGradient()"></span>
      <span class="cp-sidebar-hex">{{ overviewLabel }}</span>
    </button>
    <nav class="cp-sidebar-nav" aria-label="Palette sections">
      <button type="button"
              *ngFor="let s of pageSections"
              class="cp-sidebar-link"
              [class.active]="activeSection === s.key"
              (click)="scrollToSection( s.key )">
        {{ s.label }}
      </button>
    </nav>
  </aside>

  <div class="cp-content">

    <!-- The All In One bundle: its included themes, projected by the theme page -->
    <section class="cp-section" id="sec-includes" data-section="includes" *ngIf="bundleMode">
      <ng-content select="[cpIncludes]"></ng-content>
    </section>

    <!-- Platforms (theme pages only): one labelled screenshot per platform, in
         coverage order, three per row. Clicking a shot opens it full-screen. -->
    <section class="cp-section" id="sec-platforms" data-section="platforms" *ngIf="platformPreviews.length > 0">
      <header class="cp-head">
        <h2 class="cp-h2">Platforms</h2>
        <p class="cp-section-lead">The same palette, tuned for every app it ships on. Click a screenshot to enlarge it</p>
      </header>
      <div class="cp-main">

      <div class="cp-platform-list">
        <figure class="cp-platform" *ngFor="let preview of platformPreviews">
          <figcaption class="cp-platform-head">
            <img class="cp-platform-logo" [src]="preview.logo" alt=""
                 width="26" height="26" loading="lazy" decoding="async">
            <span class="cp-platform-name">{{ preview.name }}</span>
          </figcaption>
          <button type="button" class="cp-platform-shot" (click)="openLightbox( preview )"
                  [attr.aria-label]="'Enlarge ' + preview.name + ' screenshot'">
            <img [src]="preview.src"
                 [alt]="platformThemeName + ' theme on ' + preview.name"
                 loading="lazy" decoding="async" appImgFallback>
            <span class="img-fallback-caption">
              &#9888; Under maintenance
              <span class="img-fallback-caption-sub">We are taking care of it. Please check back soon.</span>
            </span>
          </button>
        </figure>
      </div>
      </div>
    </section>

    <!-- The theme's marketing palette (theme / theme/content pages only):
         harmony-style squares + the door into the palettes product. -->
    <section class="cp-section" id="sec-palette" data-section="palette" *ngIf="paletteLink || namedPalette.length > 0">
      <header class="cp-head">
        <h2 class="cp-h2">Color Palette</h2>
        <p class="cp-section-lead" *ngIf="namedPalette.length === 0">The signature colors of this theme. View the full palette for every named color and the downloads</p>
        <p class="cp-section-lead" *ngIf="namedPalette.length > 0">Every color of this palette, named and one click from its full breakdown</p>
      </header>
      <div class="cp-main">

      <!-- Theme pages: the 10 signature colors + the door into the palette page. -->
      <ng-container *ngIf="namedPalette.length === 0">

        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette; index as i">
            <button type="button" class="cp-swatch-color"
                    [style.background-color]="color.value"
                    (click)="openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', ''))"
                    [title]="colorNames[i] || color.value"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex">{{ color.value }}</span>
          </div>
        </div>

        <div class="cp-palette-cta" *ngIf="paletteLink">
          <div class="codigrate-button" (click)="goToPalette()">
            <p>View the Full Palette</p>
          </div>
          <!-- the theme as a wallpaper: the generator opens with these colors (a real link, so the crawler follows it) -->
          <a class="codigrate-button" [href]="wallpaperHref" target="_blank" rel="noopener" (click)="onWallpaperClick()">
            <p>Make a Wallpaper</p>
          </a>
        </div>
      </ng-container>

      <!-- Palette pages: every named color with its hex. -->
      <ng-container *ngIf="namedPalette.length > 0">

        <div class="cp-named-palette">
          <a *ngFor="let color of coreNamedColors; trackBy: trackByNamedHex"
             class="cp-named-tile"
             [routerLink]="URLS.tools.color + '/' + color.hex.substring(1)"
             [title]="color.name + ' ' + color.hex">
            <span class="cp-named-color" [style.background]="color.hex"></span>
            <span class="cp-named-text">
              <span class="cp-named-name">{{ color.name }}</span>
              <span class="cp-named-hex">{{ color.hex }}</span>
            </span>
          </a>
        </div>

        <!-- The Base24 status accents (info / success / warning / error, each
             with a focused variant), split out so they read as UI indicators. -->
        <div class="cp-named-status" *ngIf="statusNamedColors.length > 0">
          <h3 class="cp-h3 cp-named-status-head">Status Colors</h3>
          <p class="cp-named-status-note">The info, success, warning and error accents, each with its focused variant</p>
          <div class="cp-named-palette">
            <a *ngFor="let color of statusNamedColors; trackBy: trackByNamedHex"
               class="cp-named-tile"
               [routerLink]="URLS.tools.color + '/' + color.hex.substring(1)"
               [title]="color.name + ' ' + color.hex">
              <span class="cp-named-color" [style.background]="color.hex"></span>
              <span class="cp-named-text">
                <span class="cp-named-name">{{ color.name }}</span>
                <span class="cp-named-hex">{{ color.hex }}</span>
              </span>
            </a>
          </div>
        </div>
      </ng-container>
      </div>
    </section>

    <!-- Color Channels: the palette's overall color balance (aggregate). -->

    <section class="cp-section" id="sec-channels" data-section="channels" *ngIf="channelStats">
      <header class="cp-head">
        <h2 class="cp-h2">Color Channels</h2>
        <p class="cp-section-lead">Averaged across the whole palette. RGB is each channel's share of the total, plus the average HSL, HSV and CMYK</p>
      </header>
      <div class="cp-main">

      <div class="cp-channel-groups">
        <div class="cp-channel-group">
          <h3 class="cp-h3">RGB</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.rgb">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">CMYK</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.cmyk">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">HSL</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.hsl">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">HSV</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.hsv">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Harmony: a harmonic score per colour scheme (same schemes + order as the
         color tool), plus the palette's warm/cool balance. -->
    <section class="cp-section" id="sec-harmony" data-section="harmony" *ngIf="harmonyResult.length > 0 && !bundleMode">
      <header class="cp-head">
        <h2 class="cp-h2">Harmony</h2>
        <p class="cp-section-lead">A harmonic score per scheme: the share of your palette whose colors form that relationship on the wheel. Click any lit swatch to open it in the Color Explorer<ng-container *ngIf="namedPalette.length > 0">. Worked out on the theme's {{ palette.length }}-color palette, not the full {{ namedPalette.length }} named colors above</ng-container></p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let h of harmonyScores; index as hi">
        <h3 class="cp-h3 cp-harmony-head" [class.cp-h3-gap]="hi > 0" [class.cp-harmony-zero]="h.score === 0">
          <span class="cp-harmony-name">{{ h.name }}:</span>
          <span class="cp-harmony-dot" [attr.data-level]="harmonyLevel( h.score, palette.length )"></span>
          <span class="cp-harmony-score">{{ h.score }}</span>
        </h3>
        <p class="cp-scheme-desc">{{ h.description }}</p>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette; index as i">
            <button type="button" class="cp-swatch-color" [class.cp-swatch-muted]="h.counts[i] <= 0"
                    [style.background-color]="color.value"
                    (click)="h.counts[i] > 0 ? openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', '')) : null"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex">{{ h.counts[i] }}</span>
          </div>
        </div>
      </div>

      <div class="cp-scheme">
        <h3 class="cp-h3 cp-harmony-head cp-h3-gap">
          <span class="cp-harmony-name">Temperature:</span>
          <span class="cp-harmony-dot" data-level="high"></span>
          <span class="cp-harmony-score">{{ harmonyResult[ 3 ].decision | titlecase }}</span>
        </h3>
        <p class="cp-scheme-desc">Warm hues against cool ones. Their balance sets the overall mood.</p>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette; index as i">
            <button type="button" class="cp-swatch-color"
                    [class.cp-swatch-muted]="harmonyResult[3].decision !== 'equal' && temperatures[i] !== harmonyResult[3].decision"
                    [style.background-color]="color.value"
                    (click)="openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', ''))"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex cp-temp-label"
                  [class.cp-temp-minor]="harmonyResult[3].decision !== 'equal' && temperatures[i] !== harmonyResult[3].decision">{{ temperatures[ i ] }}</span>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Color vision: how the palette reads under common deficiencies. -->
    <section class="cp-section" id="sec-colorVision" data-section="colorVision" *ngIf="palette.length > 0">
      <header class="cp-head">
        <h2 class="cp-h2">Color Vision Deficiency</h2>
        <p class="cp-section-lead">How this palette reads to people with color vision deficiencies</p>
      </header>
      <div class="cp-main">

      <!-- The original palette once at the top, then each cone family below it. -->
      <div class="cp-cv-block cp-cv-original">
        <p class="cp-cv-cap"><span class="cp-cv-name">Original</span></p>
        <div class="cp-swatch-row">
          <div class="cp-swatch" *ngFor="let color of palette">
            <button type="button" class="cp-swatch-color" [style.background-color]="color.value"
                    (click)="openRouteInNewTab(URLS.tools.color + '/' + color.value.replace('#', ''))"
                    [attr.aria-label]="color.value"></button>
            <span class="cp-swatch-hex">{{ color.value }}</span>
          </div>
        </div>
      </div>

      <div class="cp-cv-group" *ngFor="let group of colorVisionGroups()">
        <h3 class="cp-h3">{{ group.group }}</h3>

        <div class="cp-cv-block" *ngFor="let item of group.items">
          <p class="cp-cv-cap"><span class="cp-cv-name">{{ item.name }}:</span> <span class="cp-cv-desc">{{ item.note }}</span></p>
          <div class="cp-swatch-row">
            <div class="cp-swatch" *ngFor="let color of item.colors">
              <button type="button" class="cp-swatch-color" [style.background-color]="color"
                      (click)="openRouteInNewTab(URLS.tools.color + '/' + color.replace('#', ''))"
                      [attr.aria-label]="color"></button>
              <span class="cp-swatch-hex">{{ color }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- From The Blog: blog poster cards, same design as the blog page hero. -->
    <!-- Related Theme (the palette pages): the theme behind this palette. -->
    <section class="cp-section" id="sec-themes" data-section="themes" *ngIf="relatedTheme">
      <header class="cp-head">
        <h2 class="cp-h2">Related Themes</h2>
        <p class="cp-section-lead">The theme these colors come from</p>
      </header>
      <div class="cp-main">

      <div class="cp-theme-grid" (click)="onRelatedThemeClick()">
        <app-theme-card [product]="relatedTheme.product"
                        [category]="relatedTheme.category"
                        [compact]="false"
                        [tile]="false"
                        [selectable]="false"
                        [clickable]="true"
                        [screenshotMode]="false">
        </app-theme-card>
      </div>
      </div>
    </section>

    <section class="cp-section" id="sec-blog" data-section="blog">
      <header class="cp-head">
        <h2 class="cp-h2">From The Blog</h2>
        <p class="cp-section-lead">Practical reads on how colors are built and combined</p>
      </header>
      <div class="cp-main">

      <div class="cp-blog-cards">
        <a class="cp-blog-card" *ngFor="let post of blogCards" [routerLink]="post.url"
           [style.--card-accent]="post.coverAccent || null">
          <img class="cp-blog-bg" [src]="post.cover" [alt]="post.title" loading="lazy" decoding="async">
          <span class="cp-blog-scrim"></span>
          <svg class="cp-blog-part" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 210 297" aria-hidden="true">
            <path d="M 31.496131,83.498219 C 17.281586,96.049685 13.920954,122.9803 24.61827,138.63631 v 0 c 8.67477,16.8641 33.95747,26.73174 51.762024,20.2132 v 0 l 54.361716,54.36558 -83.532627,83.53647 60.446297,8e-5 84.02015,-83.53655 -84.82866,-84.83262 C 113.3695,110.57782 103.49808,85.295107 86.634028,76.620298 v 0 C 80.390777,72.357466 72.362085,70.323536 64.161412,70.323536 v 0 c -12.368001,0 -25.118271,4.626039 -32.665281,13.174683"/>
          </svg>
          <span class="cp-blog-content">
            <span class="cp-blog-cat">{{ post.categoryLabel }}</span>
            <span class="cp-blog-title">{{ post.title }}</span>
            <span class="cp-blog-meta">{{ post.dateLabel }} &middot; {{ post.readingTimeMinutes }} min read</span>
          </span>
        </a>
      </div>
      </div>
    </section>

    <!-- Reviews (theme pages, owner 2026-10-04): the page's last section. The buyers' rating on
         codigrate.com (the numbers the page's structured data carries) and this theme's comments,
         from the site and the marketplaces, in the home page wall's bubbles. -->
    <section class="cp-section" id="sec-reviews" data-section="reviews" *ngIf="hasReviews">
      <header class="cp-head">
        <h2 class="cp-h2">Reviews</h2>
        <p class="cp-section-lead">What people who use this theme say about it</p>
      </header>
      <div class="cp-main">

      <!-- the way to a review: a buyer rates from the order page (owner, 2026-10-04); with no
           review yet, a kind word for the first one -->
      <div class="cp-review-cta" [class.empty]="!anyReviews" *ngIf="reviewWritable">
        <p class="cp-review-cta-text" *ngIf="!anyReviews">
          No reviews yet. Get {{ platformThemeName || 'this theme' }}, enjoy it for a few days, and be the first to tell everyone what you think.
        </p>
        <p class="cp-review-cta-text" *ngIf="anyReviews">Using {{ platformThemeName || 'this theme' }}? We would love to hear what you think.</p>
        <div class="cp-review-cta-actions">
          <a class="cp-review-cta-button" [routerLink]="URLS.orderFind">Write a Review</a>
          <button type="button" class="cp-review-cta-link" (click)="scrollToTop()">Get {{ platformThemeName || 'The Theme' }}</button>
        </div>
        <p class="cp-review-cta-note">Reviews are written from your order page. Find it with the e-mail you used.</p>
      </div>
      <app-store-reviews *ngIf="reviewBuyerCount + reviewOtherCount > 0"
                         [summary]="reviewSummary"
                         [otherCount]="reviewOtherCount"
                         [average]="reviewAverage > 0 ? reviewAverage : null"
                         [platformSummary]="reviewPlatformSummary"
                         [platformName]="reviewPlatformName"></app-store-reviews>

      <!-- the home page's wall, for this theme alone (owner, 2026-10-04) -->
      <app-review-wall [reviews]="reviewCards" label="Reviews of this theme"></app-review-wall>
      </div>
    </section>

  </div>

  <!-- Lightbox: the picked platform's screenshot enlarged over a dimmed backdrop. -->
  <div class="cp-lightbox" *ngIf="lightboxPreview" (click)="closeLightbox()">
    <!-- Image fills the fixed area above the anchored pill and centres in it, so
         the pill stays put and each screenshot sizes/positions relative to it. -->
    <figure class="cp-lightbox-inner" (click)="$event.stopPropagation()">
      <!-- Same fallback as the inline shots: a missing screenshot shows the
           maintenance illustration in its glass box instead of a broken image. -->
      <img [src]="lightboxPreview.src" [alt]="platformThemeName + ' theme on ' + lightboxPreview.name" appImgFallback>
      <span class="img-fallback-caption">
        &#9888; Under maintenance
        <span class="img-fallback-caption-sub">We are taking care of it. Please check back soon.</span>
      </span>
    </figure>
    <div class="cp-lightbox-caption-row" (click)="$event.stopPropagation()"
         [style.--pill-card-bg]="pillCardBg || null" [style.--pill-fg]="pillFg || null"
         [style.--pill-editor-bg]="pillEditorBg || null" [style.--pill-accent]="pillAccent || null"
         [style.--pill-tone]="pillTone || null">
      <button class="cp-lb-seg cp-lb-nav" type="button" (click)="stepLightbox(-1, $event)" aria-label="Previous screenshot">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4.5 L7.5 12 L15 19.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      </button>
      <figcaption class="cp-lb-seg cp-lb-platform">
        <span class="cp-lightbox-logo-circle">
          <img class="cp-lightbox-logo" [src]="lightboxPreview.logo" alt="">
        </span>
        <!-- The name area is fixed to the widest platform name (every name is
             stacked invisibly in one grid cell), so the pill never resizes as
             you page through platforms. -->
        <span class="cp-lb-name">
          <span class="cp-lb-name-sizer" *ngFor="let p of platformPreviews" aria-hidden="true">{{ p.name }}</span>
          <span class="cp-lb-name-cur">{{ lightboxPreview.name }}</span>
        </span>
      </figcaption>
      <button class="cp-lb-seg cp-lb-nav" type="button" (click)="stepLightbox(1, $event)" aria-label="Next screenshot">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4.5 L16.5 12 L9 19.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      </button>
      <button class="cp-lb-seg cp-lb-close" type="button" (click)="closeLightbox()" aria-label="Close">&times;</button>
    </div>
  </div>
</div>
